import { useContext } from "react";
import { instanceOf, node } from "prop-types";
import { action, observable } from "mobx";
import { cloneDeep, isArray } from "lodash";
import {
  createCompanyPreset,
  fetchCompanyPresetsQuery,
  removeProjectPresetQuery,
  setCompanySettingsQuery,
  setProjectPresetQuery,
  updateCompanyPresetQuery,
} from "@query";
import { COMPANY_SETTINGS } from "@client";
import { basicFonts, DEFAULT_FONT_NAME } from "@utils";
import { FontManager } from "@samuelmeuli/font-manager";
import { EstimatePresetStoreContext } from "./EstimatePresetStoreContext";
import defaultPreset from "@styles/presets/default";

// =================== Context, Provider & Hook ===================
export const EstimatePresetStoreProvider = ({ children, value }) => (
  <EstimatePresetStoreContext.Provider value={value}>
    {children}
  </EstimatePresetStoreContext.Provider>
);

export const useEstimatePresetStore = () => useContext(EstimatePresetStoreContext);
// export const useEstimatePresetStore = () => {
//   const context = useContext(EstimatePresetStoreContext);
//   if (context === null) {
//     throw new Error("EstimatePresetStoreContext cannot be null, please add a context provider");
//   }
//   return context;
// };

// @todo: potentially change this to simple state provider
// =================== Store ===================
export class EstimatePresetStore {
  constructor(
    projectPreset,
    defaultPresetId,
    useFontManager,
    setupCompanyDefaultPreset
  ) {
    if (projectPreset) this.setProjectPreset(projectPreset, useFontManager);
    if (defaultPresetId) this.defaultPresetId = Number(defaultPresetId);
    this.setupCompanyDefaultPreset = setupCompanyDefaultPreset;
  }

  @action cleanup = () => {
    this.setSelectedPreset();
    this.selectedPresetData = { ...defaultPreset };
    this.selectedPresetId = -1;
    this.selectedPresetFont = undefined;
  };
  
  @observable accessor defaultPresetId = -1;
  @observable accessor presets = [];
  @observable accessor fonts = [];
  @observable accessor presetChanged = false;
  @observable accessor selectedPresetData = { ...defaultPreset };
  @observable accessor selectedPresetId = -1;
  @observable accessor selectedPresetName = "";
  @observable accessor selectedPresetFont = null;
  @observable accessor defaultPresetFontObj;
  setupCompanyDefaultPreset;
  
  @action setFonts(fonts) {
    this.fonts = fonts;
  }
  
  @action addFont(font) {
    this.fonts = [...this.fonts, font];
  }

  @action setPresetChangedFlag(v) {
    this.presetChanged = v;
  }
  @action setDefaultPresetFontObj(defaultPresetFontObj) {
    this.defaultPresetFontObj = defaultPresetFontObj;
  }

  @action async getPresets(defaultName) {
    const res = await fetchCompanyPresetsQuery();

    this.presets = [
      {
        id: -1,
        name: defaultName,
        data: { ...defaultPreset },
      },
      ...res.map((preset) => {
        try {
          preset.data = JSON.parse(preset.params);
        } catch (e) {
          console.error(`Invalid JSON in preset ${preset.id}:`, e);
          preset.data = { ...defaultPreset };
        }
        delete preset.params;
        return preset;
      }),
    ];
    
    if(this.setupCompanyDefaultPreset && this.defaultPresetId && this.selectedPresetId === -1) {
      const preset = this.presets.find(preset => preset.id === this.defaultPresetId);
      if(preset)
        this.setSelectedPreset(preset.id, preset.name, preset ? preset.data : null, preset.font?.id ? preset.font : undefined);
    }
  }

  @action setProjectPreset({ id, name, params, font }, useFontManager=false) {
    if (!id || !params) return { ...defaultPreset };

    const d = JSON.parse(params);
    this.selectedPresetData = this.updateOldPreset(d, font);
    this.selectedPresetId = id;
    this.selectedPresetName = name;
    this.selectedPresetFont = isArray(font) ? undefined : font;

    if (
      !this.selectedPresetFont &&
      useFontManager &&
      d.fontFamily &&
      d.fontFamily !== DEFAULT_FONT_NAME &&
      !basicFonts.includes(d.fontFamily)
    ) {
      const fontManager = new FontManager(
        import.meta.env.APP_FONTS_API_KEY,
        d.fontFamily,
        { families: [d.fontFamily, DEFAULT_FONT_NAME] },
        (e, n) => {
          if (!n) return;
          fontManager.setActiveFont(n.family);
        }
      );
      fontManager.init();
      this.selectedPresetFont = { name: d.fontFamily };
    }
  }

  @action updateOldPreset(data = {}, font, presetId) {
    const fontColor = {
      ...defaultPreset.fontColor,
      ...(data?.fontColor || {}),
    };
    /** @note: backwards compatibility when both headers had single color: **/
    if (data?.fontColor?.header) {
      fontColor.breakdownHeader = data.fontColor.header;
      fontColor.summaryHeader = data.fontColor.header;
      fontColor.timelineHeader = data.fontColor.header;
      delete fontColor.header;
      this.presetChanged = true;
    }
    
    if (data?.fontColor?.summaryTableHeader) {
      fontColor.summaryHeader = data.fontColor.summaryTableHeader;
      delete fontColor.summaryTableHeader;
      this.presetChanged = true;
    }
    if (!data?.rowTimelineColor) {
      fontColor.timelineText = data.fontColor?.text;
      fontColor.timelineTitleText = data.fontColor?.titleText;
      fontColor.timelineHeader = data.fontColor?.breakdownHeader;
      data.rowTimelineColor = data.rowColor;
      data.rowActiveTimelineColor = data.activeColor1;
      data.rowTimelineBorderColor = data.rowBorderColor;
      this.presetChanged = true;
    }
    
    if (data?.hoverColor1) {
      data.activeColor1 = data.hoverColor1;
      delete data.hoverColor1
      this.presetChanged = true;
    }
    if (data?.hoverColor2) {
      data.activeColor2 = data.hoverColor2;
      delete data.hoverColor2
      this.presetChanged = true;
    }
    if (data?.hoverColor3) {
      data.activeColor3 = data.hoverColor3;
      delete data.hoverColor3
      this.presetChanged = true;
    }
    
    if (data?.text && !data.titleText) {
      data.titleText = data.text;
      data.summaryText = data.text;
      data.summaryTitleText = data.text;
      this.presetChanged = true;
    }
    
    if(data?.fontColor && !data?.fontColor?.sectionRow) {
      fontColor.sectionRow = data.fontColor?.section;
      fontColor.moreDesc = data.fontColor?.section;
      fontColor.moreDescSection = data.fontColor?.section;
      fontColor.titleTextSection = data.fontColor?.section;
      this.presetChanged = true;
    }
    
    if(data?.fontColor && !data?.fontColor?.totalCost) {
      fontColor.totalTime = data.fontColor?.total;
      fontColor.totalHours = data.fontColor?.total;
      fontColor.totalCost = data.fontColor?.total;
      fontColor.totalTimeTitle = data.fontColor?.summaryHeader;
      fontColor.totalHoursTitle = data.fontColor?.summaryHeader;
      fontColor.totalCostTitle = data.fontColor?.summaryHeader;
      this.presetChanged = true;
    }
    
    if(!data?.paperTimelineColor) {
      data.paperTimelineColor = data.paperColor;
      this.presetChanged = true;
    }
    
    if(fontColor?.tableHeader) {
      fontColor.tableBreakdownHeader = fontColor.tableHeader;
      fontColor.tableTimelineHeader = fontColor.tableHeader;
      fontColor.tableTimelineBilling = fontColor.tableHeader;
      fontColor.tableSummaryHeader = fontColor.tableHeader;
      delete fontColor.tableHeader;
      this.presetChanged = true;
    }
    
    if(data?.tableExpandButton) {
      data.tableBreakdownExpandButton = data.tableExpandButton;
      data.tableSummaryExpandButton = data.tableExpandAltButton;
      data.tableTimelineExpandButton = data.tableExpandAltButton;
      fontColor.tableBreakdownExpandButton = fontColor.tableExpandButton;
      fontColor.tableSummaryExpandButton = fontColor.tableExpandAltButton;
      fontColor.tableTimelineExpandButton = fontColor.tableExpandAltButton;
      delete data.tableExpandButton;
      delete data.tableExpandAltButton;
      delete fontColor.tableExpandButton;
      delete fontColor.tableExpandAltButton;
      this.presetChanged = true;
    }
    
    if(!data?.paperDescriptionBackground) {
      data.paperDescriptionBackground = data.paperColor;
      data.textDescriptionExpandButton = data.tableSummaryExpandButton;
      fontColor.textDescriptionHeader = data.fontColor.summaryHeader;
      fontColor.textDescriptionExpandButton = data.fontColor.tableSummaryExpandButton;
      this.presetChanged = true;
    }

    if (presetId) {
      const toUpdate = this.presets.find((preset) => preset.id === presetId);
      if (toUpdate) toUpdate.data = { ...defaultPreset, ...data, fontColor };
    }
    
    return data;
  }

  @action async setSelectedPreset(id = -1, name = "", data = {}, font) {
    if (this.presetChanged) {
      await this.updatePreset();
      this.presetChanged = false;
    }
    this.selectedPresetData = this.updateOldPreset(data, font, id);
    this.selectedPresetId = id;
    this.selectedPresetName = name;
    this.selectedPresetFont = id === -1 && this.defaultPresetFontObj ? { ...this.defaultPresetFontObj } : font;
  }
  
  @action setSelectedPresetFont(fontObj, noPresetChangeFlag=false) {
    this.selectedPresetFont = fontObj;
    if(this.selectedPresetId !== -1 || !noPresetChangeFlag)
      this.presetChanged = true;
  }

  @action changePresetData(key, val) {
    this.selectedPresetData[key] = val;
    this.presetChanged = true;
    if (this.selectedPresetId !== -1) {
      const pr = this.presets.find(
        (preset) => preset.id === this.selectedPresetId
      );
      if (pr) pr.data[key] = val;
    }
  }

  @action changePresetFontColor(key, val) {
    this.selectedPresetData.fontColor[key] = val;
    this.presetChanged = true;
    if (this.selectedPresetId !== -1) {
      const pr = this.presets.find(
        (preset) => preset.id === this.selectedPresetId
      );
      if (pr) pr.data.fontColor[key] = val;
    }
  }
 
  @action changePresetText(key, val) {
    if(!this.selectedPresetData.texts)
      this.selectedPresetData.texts = {};
    this.selectedPresetData.texts[key] = val;
    this.presetChanged = true;
    if (this.selectedPresetId !== -1) {
      const pr = this.presets.find(
        (preset) => preset.id === this.selectedPresetId
      );
      if (pr) {
        if (!pr.data.texts) pr.data.texts = {};
        pr.data.texts[key] = val;
      }
    }
  }
  
  @action removeLogo() {
    delete this.selectedPresetData.logo;
    if (this.selectedPresetId !== -1) {
      const pr = this.presets.find(
        (preset) => preset.id === this.selectedPresetId
      );
      if (pr) delete pr.data.logo;
    }
  }
  @action setLogo(logo) {
    this.selectedPresetData.logo = logo;
    if (this.selectedPresetId !== -1) {
      const pr = this.presets.find(
        (preset) => preset.id === this.selectedPresetId
      );
      if (pr) pr.data.logo = logo;
    }
  }
  
  @action async setDefaultPresetId(defaultPresetId) {
    this.defaultPresetId = defaultPresetId;
    await setCompanySettingsQuery(COMPANY_SETTINGS.PRESET, defaultPresetId);
  }

  @action resetDefaultPreset() {
    this.selectedPresetData = { ...defaultPreset };
    this.selectedPresetFont = this.defaultPresetFontObj ? {...this.defaultPresetFontObj} : undefined;
    this.presetChanged = false;
  }

  @action async removePreset(id) {
    this.presets = this.presets.filter(p => p.id !== id);
  }

  @action async addPresetToProject(projectUuid) {
    if (this.selectedPresetId === -1) {
      try {
        await removeProjectPresetQuery(projectUuid); // @note: api should not treat it as an error?
      } catch(e) {
        console.warn("Preset already removed");
      }
      return;
    }

    await setProjectPresetQuery(projectUuid, this.selectedPresetId);
  }

  @action async createPreset(name) {
    const res = await createCompanyPreset(name, this.selectedPresetData, this.selectedPresetFont?.id);
    if (res) {
      this.selectedPresetId = res.id;
      this.selectedPresetName = name;
      this.presetChanged = false;
      this.presets.push({
        ...res,
        data: cloneDeep(this.selectedPresetData),
      });
      return res;
    }
  }

  @action async updatePreset() {
    this.presetChanged = false;
    if (this.selectedPresetId !== -1)
      await updateCompanyPresetQuery(
        this.selectedPresetId,
        this.selectedPresetName,
        this.selectedPresetData,
        this.selectedPresetFont?.id
      );
  }
}

EstimatePresetStoreProvider.propTypes = {
  children: node.isRequired,
  value: instanceOf(EstimatePresetStore).isRequired,
};